import React, {
  ReactElement,
  useState,
  CSSProperties,
  useMemo,
  useCallback,
} from 'react';
import cn from 'classnames';
import useTouchHandlers from './useTouchHandlers';

interface Props {
  onTouch?: (e: React.TouchEvent) => void;
  onClick?: (e: React.TouchEvent) => void;
  disabled?: boolean;
  children: ReactElement;
}

export default function <T>(
  renderActiveStyle: (props: T) => CSSProperties,
  touchableClassName: string
): React.ComponentType<Props & T> {
  return function (props: Props & T) {
    const [touchStyle, setTouchStyle] = useState<CSSProperties>({});

    const { onClick, onTouch, disabled, children } = props;

    const handleActive = useCallback(() => {
      setTouchStyle(renderActiveStyle(props));
    }, [props]);

    const handleInActive = useCallback(() => {
      setTouchStyle({});
    }, []);

    const handlers = useTouchHandlers(handleActive, handleInActive, disabled);

    const style = useMemo(() => {
      return {
        ...children.props.style,
        ...touchStyle,
      };
    }, [children.props.style, touchStyle]);

    const handleClick = useCallback(
      (e: React.TouchEvent) => {
        onClick && onClick(e);
        onTouch && onTouch(e);
        children.props.onClick && children.props.onClick(e);
      },
      [children.props, onClick, onTouch]
    );

    if (React.isValidElement(children) === false) return null;

    return React.cloneElement(children, {
      ...handlers,
      className: cn(children.props.className, touchableClassName),
      style,
      onClick: handleClick,
    });
  };
}
